import { supabase } from "@/integrations/supabase/client";

export async function exportFundList() {
  // Get all funds with their company information
  const { data: funds, error } = await supabase
    .from('mv_funds_raw_categories')
    .select(`*`);

  if (error) {
    console.error('Error fetching funds:', error);
    return null;
  }

  // Get performance data for all funds
  const fundNames = (funds ?? []).map((f: any) => f.fund_name).filter(Boolean);
  let performances: any[] = [];
  if (fundNames.length) {
    const { data: perfData, error: perfError } = await supabase
      .from('fund_performance')
      .select('*')
      .in('fund_name', fundNames);

    if (perfError) {
      console.error('Error fetching performances:', perfError);
      return null;
    }
    performances = perfData ?? [];
  }

  // Format data for research template
  const researchTemplates = (funds ?? []).map((fund: any) => {
    const fundPerformance = performances.filter(p => p.fund_name === fund.fund_name);
    
    // Calculate date range of existing performance data
    const performanceDates = fundPerformance.map(p => new Date(p.date).getTime());
    const earliestDate = performanceDates.length > 0 ? new Date(Math.min(...performanceDates)) : null;
    const latestDate = performanceDates.length > 0 ? new Date(Math.max(...performanceDates)) : null;
    
    return {
      company: {
        fund_company_name: fund.company_name || '',
        website_url: fund.company_website_url || '',
        description: ''
      },
      fund_name: fund.fund_name,
      fund_code: fund.fund_code ?? null,
      inception_date: null,
      fund_size: null,
      management_fee: null,
      performance_fee: null,
      distributions: null,
      investment_strategy: '',
      fund_framework: '',
      description: null,
      class_name: null,
      
      // Include existing performance data summary
      existing_data_coverage: {
        start_date: earliestDate?.toISOString().split('T')[0] || 'No data',
        end_date: latestDate?.toISOString().split('T')[0] || 'No data',
        metrics: Array.from(new Set(fundPerformance.map((p: any) => p.metric_name))),
        total_records: fundPerformance.length
      }
    };
  });

  // Create a focused research prompt template
  const researchPrompt = `
Research Request: Historical Performance Data Extension for Canadian Alternative Investment Funds

Objective: Extend historical performance data for the following funds, focusing specifically on periods BEFORE our existing data coverage. We already have high-quality recent performance data and need to expand our historical coverage.

Key Requirements:
1. Focus ONLY on historical data BEFORE our existing coverage dates
2. Prioritize monthly returns data where available
3. Include risk metrics (Sharpe ratio, volatility) if available for historical periods
4. All data must be verified against official fund documents or reliable industry sources
5. Data format must match our existing structure for consistency

Funds to Research:

${researchTemplates.map((template, index) => `
Fund ${index + 1}: ${template.fund_name}
Company: ${template.company.fund_company_name}
Website: ${template.company.website_url}
Current Data Coverage: ${template.existing_data_coverage.start_date} to ${template.existing_data_coverage.end_date}
Existing Metrics: ${template.existing_data_coverage.metrics.join(', ')}
Records Count: ${template.existing_data_coverage.total_records}

Research Priority: Historical data before ${template.existing_data_coverage.start_date}
`).join('\n')}

Important Notes:
- We already have comprehensive data coverage for recent periods
- Focus exclusively on extending historical data before our earliest dates
- Verify all historical data against official sources
- Maintain consistency with our existing data format and quality standards

Please provide the historical data in a structured JSON format matching our template for each fund.
`;

  return {
    funds: researchTemplates,
    researchPrompt
  };
}
